import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    getDocs,
    query,
    where
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardListIcon,
    XCircleIcon,
    EyeIcon
} from '@heroicons/react/outline';
import { TrophyIcon } from 'lucide-react'
import { useAuth } from '../../../../Contexts/AuthContext';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();
    const userRole = userData?.role || "Judge";
    const [dashboardData, setDashboardData] = useState({
        totalApplications: 0,
        totalTeams: 0,
        totalMentors: 0,
        totalJudges: 0,
        pendingApplications: 0, // not yet evaluated
        evaluatedApplications: 0
    });

    const [topApplications, setTopApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [applicationsPerPage, setApplicationsPerPage] = useState(5);
    const [totalApplications, setTotalApplications] = useState(0);
    const [paginatedApplications, setPaginatedApplications] = useState([]);

    const fetchTopApplications = useCallback(async () => {
        try {
            console.log('Fetching top evaluated applications...');
            console.log('User role:', userRole);

            const applicationsQuery = query(
                collection(db, 'hackathonApplications'),
                where('isEvaluated', '==', true)
            );

            const querySnapshot = await getDocs(applicationsQuery);
            const applications = [];

            // Get judge's assigned categories if applicable
            const judgeCategories = userRole === 'Judge' && userData?.categoryAssignments
                ? userData.categoryAssignments.map(assignment => assignment.category)
                : null;

            console.log('Judge categories:', judgeCategories);

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.evaluation && data.evaluation.score !== undefined) {
                    // If user is a Judge, filter by assigned categories
                    if (userRole === 'Judge' && judgeCategories) {
                        if (data.category && judgeCategories.includes(data.category)) {
                            applications.push({
                                id: doc.id,
                                ...data
                            });
                        }
                    } else {
                        // For Admin/Mentor or if no category assignments, show all
                        applications.push({
                            id: doc.id,
                            ...data
                        });
                    }
                }
            });

            // Sort by score in descending order
            applications.sort((a, b) => {
                const scoreA = a.evaluation?.score || 0;
                const scoreB = b.evaluation?.score || 0;
                return scoreB - scoreA;
            });

            console.log('Top applications fetched and sorted:', applications.length);
            setTopApplications(applications);
            setTotalApplications(applications.length);

            // Immediately paginate the fetched applications
            const startIndex = (currentPage - 1) * applicationsPerPage;
            const endIndex = startIndex + applicationsPerPage;
            const paginated = applications.slice(startIndex, endIndex);
            setPaginatedApplications(paginated);
        } catch (error) {
            console.error('Error fetching top applications:', error);
        }
    }, [userRole, userData?.categoryAssignments, currentPage, applicationsPerPage]);

    const fetchDashboardData = useCallback(async () => {
        try {
            console.log('Fetching dashboard data...');
            setError(null);

            // Get judge's assigned categories if applicable
            const judgeCategories = userRole === 'Judge' && userData?.categoryAssignments
                ? userData.categoryAssignments.map(assignment => assignment.category)
                : null;

            // Fetch total applications
            const applicationsQuery = query(collection(db, 'hackathonApplications'));
            console.log('Fetching applications...');
            const applicationsSnapshot = await getDocs(applicationsQuery);

            let totalApplications = 0;
            let evaluatedApplications = 0;
            let pendingApplications = 0;

            applicationsSnapshot.forEach((doc) => {
                const data = doc.data();

                // If user is a Judge, filter by assigned categories
                if (userRole === 'Judge' && judgeCategories) {
                    if (data.category && judgeCategories.includes(data.category)) {
                        totalApplications++;
                        if (data.isEvaluated) {
                            evaluatedApplications++;
                        } else {
                            pendingApplications++;
                        }
                    }
                } else {
                    // For Admin/Mentor, count all applications
                    totalApplications++;
                    if (data.isEvaluated) {
                        evaluatedApplications++;
                    } else {
                        pendingApplications++;
                    }
                }
            });

            console.log('Total applications found:', totalApplications);
            console.log('Application counts:', { pendingApplications, evaluatedApplications });

            // Fetch total teams
            const teamsQuery = query(collection(db, 'teams'));
            const teamsSnapshot = await getDocs(teamsQuery);
            const totalTeams = teamsSnapshot.size;
            console.log('Total teams found:', totalTeams);

            // Fetch total mentors
            const mentorsQuery = query(collection(db, 'users'), where('role', '==', 'mentor'));
            const mentorsSnapshot = await getDocs(mentorsQuery);
            const totalMentors = mentorsSnapshot.size;
            console.log('Total mentors found:', totalMentors);

            // Fetch total judges
            const judgesQuery = query(collection(db, 'users'), where('role', '==', 'judge'));
            const judgesSnapshot = await getDocs(judgesQuery);
            const totalJudges = judgesSnapshot.size;
            console.log('Total judges found:', totalJudges);

            console.log('Fetched dashboard data:', {
                totalApplications,
                totalTeams,
                totalMentors,
                totalJudges,
                pendingApplications,
                evaluatedApplications,
            });

            setDashboardData({
                totalApplications,
                totalTeams,
                totalMentors,
                totalJudges,
                pendingApplications,
                evaluatedApplications,
            });


            console.log('Setting loading to false');
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message);
            setLoading(false);
        }
    }, [userRole, userData?.categoryAssignments]);

    useEffect(() => {
        console.log('AdminDashboard component mounted');
        fetchDashboardData();
        fetchTopApplications();
    }, [fetchDashboardData, fetchTopApplications]);

    // Re-paginate when pagination settings change
    useEffect(() => {
        paginateApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, applicationsPerPage, topApplications]);

    const paginateApplications = () => {
        const startIndex = (currentPage - 1) * applicationsPerPage;
        const endIndex = startIndex + applicationsPerPage;
        const paginated = topApplications.slice(startIndex, endIndex);
        setPaginatedApplications(paginated);
    };

    const handlePageChange = (page) => {
        if (page === currentPage) return;
        setCurrentPage(page);
    };

    const handleApplicationsPerPageChange = (newPerPage) => {
        setApplicationsPerPage(newPerPage);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalApplications / applicationsPerPage);

    console.log('AdminDashboard render - loading:', loading, 'error:', error);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Error loading dashboard
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>{error}</p>
                                </div>
                                <div className="mt-4">
                                    <button
                                        onClick={() => {
                                            setError(null);
                                            setLoading(true);
                                            fetchDashboardData();
                                        }}
                                        className="bg-red-100 px-2 py-1 text-sm font-medium text-red-800 rounded-md hover:bg-red-200"
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Hello, Admin 👋</h1>
                    <p className="text-gray-600 mt-1">Here's what's happening with your hackathon today.</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Applications */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                                <p className="text-3xl font-bold text-custom-blue-1 mt-2">{dashboardData.totalApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50">
                                <ClipboardListIcon className="h-8 w-8 text-custom-blue-1" />
                            </div>
                        </div>
                    </div>
                    {/* Pending Evaluation */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Pending Evaluation</p>
                                <p className="text-3xl font-bold text-orange-500 mt-2">{dashboardData.pendingApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-orange-50">
                                <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Evaluated */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Evaluated</p>
                                <p className="text-3xl font-bold text-purple-600 mt-2">{dashboardData.evaluatedApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-purple-50">
                                <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Total Teams */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hidden lg:block">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Teams</p>
                                <p className="text-3xl font-bold text-indigo-600 mt-2">{dashboardData.totalTeams}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-indigo-50">
                                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Applications Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <TrophyIcon className="h-6 w-6 text-yellow-500" />
                                    Top Evaluated Applications
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">Ranked by evaluation score</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                                    Show:
                                </label>
                                <select
                                    id="itemsPerPage"
                                    value={applicationsPerPage}
                                    onChange={(e) => handleApplicationsPerPageChange(Number(e.target.value))}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className="text-sm text-gray-600">per page</span>
                            </div>
                        </div>
                    </div>

                    {/* Results Summary */}
                    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                        <p className="text-sm text-gray-600">
                            Showing {paginatedApplications.length > 0 ? ((currentPage - 1) * applicationsPerPage) + 1 : 0} to{' '}
                            {Math.min(currentPage * applicationsPerPage, totalApplications)} of {totalApplications} evaluated applications
                        </p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rank
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Team Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Team Lead
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Score
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Evaluated By
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedApplications.length > 0 ? (
                                    paginatedApplications.map((application, index) => {
                                        const rank = (currentPage - 1) * applicationsPerPage + index + 1;
                                        const score = application.evaluation?.score || 0;

                                        return (
                                            <tr key={application.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        {rank <= 5 ? (
                                                            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${rank === 1 ? 'bg-yellow-100' : rank === 2 ? 'bg-gray-100' : 'bg-orange-100'
                                                                }`}>
                                                                <span className={`text-lg font-bold ${rank === 1 ? 'text-yellow-600' : rank === 2 ? 'text-gray-600' : 'text-orange-600'
                                                                    }`}>
                                                                    {rank}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm font-medium text-gray-600">#{rank}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {application.teamName || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {application.category && `${application.category}`}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {application.teamLeaderName || application.fullName || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            Team Lead • {application.teamLeaderEmail || application.email || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className={`text-2xl font-bold ${score >= 80 ? 'text-green-600' :
                                                            score >= 60 ? 'text-blue-600' :
                                                                score >= 40 ? 'text-yellow-600' :
                                                                    'text-red-600'
                                                            }`}>
                                                            {score}
                                                        </div>
                                                        <span className="text-sm text-gray-500 ml-1">/100</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {application.evaluation?.evaluator || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {formatDate(application.evaluation?.evaluatedAt)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => navigate(`/eventdashboard/application-details/${application.id}`, {
                                                            state: { application },
                                                        })}
                                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-custom-blue-1 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                                                    >
                                                        <EyeIcon className="w-4 h-4 mr-1" />
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                            No evaluated applications found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                        let pageNumber;
                                        if (totalPages <= 7) {
                                            pageNumber = i + 1;
                                        } else if (currentPage <= 4) {
                                            pageNumber = i + 1;
                                        } else if (currentPage >= totalPages - 3) {
                                            pageNumber = totalPages - 6 + i;
                                        } else {
                                            pageNumber = currentPage - 3 + i;
                                        }

                                        if (pageNumber < 1 || pageNumber > totalPages) return null;

                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => handlePageChange(pageNumber)}
                                                className={`px-3 py-2 text-sm font-medium rounded-md ${pageNumber === currentPage
                                                    ? 'bg-custom-blue-1 text-white'
                                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>

                            <div className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );

    // Helper functions
    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }
};

export default AdminDashboard;
